import './ShiftView.scss';

import * as dateFns from 'date-fns';

import {
  AbsenceType,
  CallIn,
  Exchange,
  Team,
  TeamDuty,
  TeamShift,
  TeamShiftDef,
} from '@pdcplan/shared/api/TeamPlan';
import { EmployeeMap, ShiftMap, TeamMap } from '../api/model';
import {
  ExchangeCandidateFilterEnum,
  ExchangeTypeEnum,
  TeamShiftStatusEnum,
} from '@pdcplan/shared/enums';
import Icons, { IconSize } from '@pdcfrontendui/components/Icons';
import ShiftCard, { ShiftInfo } from './ShiftCard';
import {
  ShiftDraft,
  ShiftDraftType,
  calculateModifiedDutylines,
  isCreatedShift,
  isEditedShift,
} from '../ListView/CreateShift/EditedShift';
import {
  ChatType,
  TeamViewMode,
  TeamViewQueries,
  getSiteRoutes,
  useTeamRouteParams,
} from '../routes';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ActiveOfferView } from './ShiftViewEnums';
import BusinessCard from '../components/BusinessCard';
import Card from '../components/Card';
import Chat from '../Chat';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { type DayMessageMap } from '../util/getChatKey';
import OfferView from './OfferView';
import { Period } from '../util/dates';
import { ShiftViewProps } from './ShiftViewContainer';
import SwapView from './SwapView';
import { currentLanguage } from '../currentLanguage';
import getApi from '../getApi';
import ids from '../testing/ids';
import { useAsync } from '@pdcfrontendui/hooks';
import { useNavigate } from 'react-router-dom';
import useNotesAndMessages from '../hooks/useNotesAndMessages';
import { useFormatter } from '../hooks/useLang';

export type StateFromProps = {
  loading: boolean;
  loadingEmployee: boolean;
  loadingSwap: boolean;
  team: Team | null;
  shift: TeamShift | null;
  employees: EmployeeMap;
  absenceTypes: AbsenceType[];
  currentDate: Date;
  dayMessageMap: DayMessageMap;
  userId: number;
  shifts: ShiftMap;
  swapDetails: Exchange | null;
  isSwapViewOpen: boolean;
  pendingAcceptRequest: boolean;
  pendingRejectRequest: boolean;
  substitutes: CallIn[];
  selectedExchangeId: number | null;
  acceptRequestEnabled: boolean;
  teams: TeamMap;
  shiftDraft: ShiftDraft | null;
  possibleCandidateFilters: ExchangeCandidateFilterEnum[];
  defaultExchangeCandidateFilter: ExchangeCandidateFilterEnum;
  showConfirmDeleteShift: boolean;
  deleteShiftLoading: boolean;
  shiftDefMap: Record<string, TeamShiftDef>;
  offerViewActiveView: ActiveOfferView;
  canSeeConfidentialInfo: boolean;
};

export type DispatchFromProps = {
  callMarkAsHandled: (
    teamId: string,
    shiftId: string,
    personId: number
  ) => void;
  callOfferOnShiftExchange: (
    shiftId: string,
    currentTeamId: string,
    teamIdList: string[],
    candidateFilter: ExchangeCandidateFilterEnum,
    remark: string
  ) => void;
  callRegisterAbsence: (
    teamId: string,
    shiftId: string,
    absenceTypeId: string,
    returnDate: Date | null
  ) => void;
  callAssignShift: (
    teamId: string,
    personId: number,
    employeeName: string,
    shiftId: string,
    updatedPeriod: Period | null,
    currentlyOnFictive: boolean
  ) => void;
  callAssignCreatedShift: (
    teamId: string,
    personId: number,
    employeeName: string
  ) => void;
  loadAbsenceTypes: (shiftId: string, teamId: string) => void;
  callGetEmployees: (personIds: number[], teamId: string) => void;
  callPostMessage: (
    personId: number,
    messageText: string,
    isInternalMessage: boolean,
    day: Date,
    sentWhen: Date
  ) => void;
  getExchange: (personId: number, shiftStart: Date) => void;
  callExchangeAccept: (
    unitId: string,
    requestExchangePeriod: Period,
    isSwap: boolean,
    offerPersonId: number,
    offerPersonName: string,
    offerExchangeId: number,
    requestPersonId: number | null,
    requestPersonName: string
  ) => void;
  setSwapViewOpen: (open: boolean) => void;
  setOfferViewOpen: (open: boolean) => void;
  setActiveOfferView: (activeOfferView: ActiveOfferView) => void;
  callExchangeReject: (
    exchangeId: number,
    exchangePeriod: Period,
    exchangeUnitId: string,
    exchangeType: ExchangeTypeEnum,
    remark: string | null,
    isSwap: boolean
  ) => void;
  callCancelOfferOnShiftExchange: (
    teamId: string,
    personId: number,
    shiftId: string
  ) => void;
  exchangeSelectExchangeId: (exchangeId?: number) => void;
  attemptRemoveShift: (
    shiftId: string,
    teamId: string,
    employeeName: string,
    shiftLabel: string,
    shiftTimeLabel: string
  ) => Promise<void>;
  setConfirmDeleteModal: (shown: boolean) => void;
  attemptEndAbsence: (
    teamId: string,
    shift: TeamShift,
    employeeName: string
  ) => void;
  setShiftDraft: (draft: ShiftDraft | null) => void;
};

const ShiftView = ({
  employees,
  personId,
  team,
  shift,
  absenceTypes,
  loading,
  loadingEmployee,
  loadingSwap,
  currentDate,
  isSwapViewOpen,
  offerViewActiveView,
  selectedExchangeId,
  dayMessageMap,
  userId,
  shifts,
  swapDetails,
  pendingAcceptRequest,
  pendingRejectRequest,
  substitutes,
  acceptRequestEnabled,
  teams,
  possibleCandidateFilters,
  defaultExchangeCandidateFilter,
  deleteShiftLoading,
  showConfirmDeleteShift,
  shiftDefMap,
  shiftDraft,
  canSeeConfidentialInfo,
  callRegisterAbsence,
  loadAbsenceTypes,
  getExchange,
  callGetEmployees,
  callMarkAsHandled,
  callAssignShift: _callAssignShift,
  callPostMessage,
  setSwapViewOpen,
  setOfferViewOpen,
  callExchangeAccept,
  callExchangeReject,
  callCancelOfferOnShiftExchange,
  callOfferOnShiftExchange,
  attemptRemoveShift,
  exchangeSelectExchangeId,
  attemptEndAbsence: _attemptEndAbsence,
  setConfirmDeleteModal,
  setShiftDraft,
  callAssignCreatedShift,
  setActiveOfferView,
}: DispatchFromProps & StateFromProps & ShiftViewProps) => {
  // When accepting a swap, the shift is removed before the url is updated.
  // that puts us in a state where for a bit shift is not set (as it's trying to get)
  // a non existing shift. We can redesign the accept/refetch flow to prevent this.
  if (!shift || !team) return null;

  const navigate = useNavigate();
  const params = useTeamRouteParams();

  /**
   * Partial update of the URL parameters.
   */
  const updateParams = useCallback(
    (updatedParams: Partial<TeamViewQueries>) => {
      navigate(
        getSiteRoutes().team(team.id, {
          ...params,
          ...updatedParams,
        })
      );
    },
    [navigate, params, team.id]
  );
  const formatter = useFormatter();

  const callFindSubstitute = (
    teamId: string,
    shiftId: string,
    personId: number
  ) => {
    updateParams({
      shiftId,
      personId,
      mode: TeamViewMode.FindSubstitute,
    });
  };

  const openChat = (chat: ChatType) => {
    updateParams({ chat });
  };

  const closeChat = () => {
    updateParams({ chat: undefined });
  };

  const substitute =
    params.mode === TeamViewMode.FindSubstitute
      ? substitutes.find((f) => f.id === personId)
      : undefined;

  // If we are assigning a shift, we want to show if the recipient has shifts on the given day, and so we have to do a bit of a search
  // TODO CHHI: This seems overly expensive, and should be done differently
  const shiftsForRecipient = useMemo(
    () =>
      substitute
        ? Object.values(shifts).filter(
            (currentShift) =>
              currentShift.personId === personId &&
              dateFns.isSameDay(currentShift.period.from, shift.period.from)
          )
        : null,
    [personId, shift.period.from, substitute, shifts]
  );

  // Pre-fetch deleted activities
  const { value: deletedActivities, hasError: coudNotFetchDeletedActivities } =
    useAsync(
      shift.activities.length
        ? () =>
            getApi().getRemovedRegistrationInfo(
              team.id,
              isEditedShift(shiftDraft)
                ? shiftDraft.originalShift.id
                : shift.id,
              true,
              null
            )
        : null,
      [shift.activities.length, shift.id, team.id]
    );

  useEffect(() => {
    if (!shiftDraft && shift.id && team.id) {
      loadAbsenceTypes(shift.id, team.id);
    }
  }, [shift.id, team.id, loadAbsenceTypes, shiftDraft, params.mode]);

  useEffect(() => {
    if (
      shift.status === TeamShiftStatusEnum.offerActionRequired ||
      shift.status === TeamShiftStatusEnum.swapActionRequired
    ) {
      getExchange(personId, shift.period.from);
    }
  }, [shift.status, personId, shift.period.from, getExchange]);

  const setEditedShift = useCallback(
    (shiftDraft: ShiftDraft | null) => {
      setShiftDraft(shiftDraft);
      updateParams({
        mode: TeamViewMode.CreateOrEdit,
      });
    },
    [setShiftDraft, updateParams]
  );

  const {
    messages,
    numMessages,
    hasUnreadMessages,
    sendMessage,
    internalMessages,
    numInternalMessages,
    hasUnreadInternalMessages,
    sendInternalMessage,
  } = useNotesAndMessages(
    userId,
    dayMessageMap,
    shift.period.from,
    personId,
    callPostMessage,
    currentDate // TODO: Figure out sent time for temp message
  );

  const isSwap = swapDetails?.requestInfo.type === ExchangeTypeEnum.swapRequest;
  const employee = employees[personId];

  const callAssignShift = useCallback(
    (
      teamId: string,
      personId: number,
      employeeName: string,
      shiftId: string
    ) => {
      if (isEditedShift(shiftDraft)) {
        _callAssignShift(
          teamId,
          personId,
          employeeName,
          shiftDraft.originalShift.id,
          { from: shiftDraft.from, to: shiftDraft.to },
          false
        );
      } else if (isCreatedShift(shiftDraft)) {
        callAssignCreatedShift(teamId, personId, employeeName);
      } else if (!shiftDraft) {
        const currentEmployeeForShift = employees[shift.personId];
        _callAssignShift(
          teamId,
          personId,
          employeeName,
          shiftId,
          null,
          !!currentEmployeeForShift?.isFictive
        );
      }
    },
    [
      _callAssignShift,
      shift.personId,
      employees,
      callAssignCreatedShift,
      shiftDraft,
    ]
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      setScrollbarWidth(
        Math.round(
          scrollContainerRef.current.offsetWidth -
            scrollContainerRef.current.clientWidth
        )
      );
    }
  }, []);

  // 12 is the default padding, which needs to be subtracted
  const paddingRight = `${Math.min(scrollbarWidth - 12, 12)}px`;

  const shiftDef = shiftDefMap[shift.shiftDefId] ?? null;

  const attemptEndAbsence = () => {
    _attemptEndAbsence(team.id, shift, employee?.name ?? '');
  };

  const actionRequired =
    !substitute && shift.status === TeamShiftStatusEnum.actionRequired;

  // Show original duties if shift is assignable, and not the absense
  // And if action is required, show the original duties since the absence duties are shown on a separate card
  const dutyLines: TeamDuty[] = [
    ...((actionRequired || !!substitute
      ? shift.dutyLinesOriginal
      : shift.dutyLines) ?? []),
  ].sort((a, b) => {
    if (a.start === null && b.start === null) return 0;
    if (a.start === null) return 1;
    if (b.start === null) return -1;
    return a.start - b.start;
  });

  return (
    <>
      <div className="shiftview-container" id={ids.ShiftView.container}>
        {employee && (
          <BusinessCard employee={employee} loadingEmployee={loadingEmployee} />
        )}
        <div
          ref={scrollContainerRef}
          className="content"
          style={{
            paddingRight,
          }}
        >
          {employee && !employee.isFictive && (
            <>
              <Card
                badge={numMessages}
                label={currentLanguage.ChatWithEmployee}
                onClick={() => {
                  openChat(ChatType.Employee);
                }}
                newBadge={hasUnreadMessages}
                id={ids.ShiftView.chat}
              >
                <Icons.Chat size={IconSize.XSmall} />
              </Card>
              <Card
                badge={numInternalMessages}
                label={currentLanguage.AddNote}
                onClick={() => {
                  openChat(ChatType.Private);
                }}
                newBadge={hasUnreadInternalMessages}
                id={ids.ShiftView.internalMessage}
              >
                <Icons.ChatLock size={IconSize.XSmall} />
              </Card>
            </>
          )}
          {shiftsForRecipient?.map((recipientShift) => (
            <ShiftInfo
              key={recipientShift.id}
              absenceLabel={
                absenceTypes.find(
                  (absenceType) => absenceType.id === recipientShift.type
                )?.label
              }
              dutyLines={recipientShift.dutyLines}
              shift={recipientShift}
              shiftDraft={shiftDraft}
              swapDetails={null}
            />
          ))}
          <ShiftCard
            {...{
              absenceTypes,
              callAssignShift,
              callFindSubstitute,
              callMarkAsHandled,
              callRegisterAbsence,
              callOfferOnShiftExchange,
              callCancelOfferOnShiftExchange,
              currentDate,
              personId,
              employees,
              loading,
              shift,
              team,
              loadingSwap,
              setSwapViewOpen,
              setOfferViewOpen,
              setActiveOfferView,
              substitute,
              dutyLines,
              onRemoveShift: () => {
                setConfirmDeleteModal(true);
              },
              setEditedShift,
              shiftDef,
              shiftDraft,
              attemptEndAbsence,
              exchangeType: swapDetails?.requestInfo.type ?? null,
              swapDetails,
            }}
          />
        </div>
      </div>
      <Chat
        title={`${currentLanguage.ChatWith_1(
          employees[personId]?.name.split(' ')[0] ?? currentLanguage.Employee
        )}, ${formatter.dayMonth(shift.period.from)}`}
        goBack={closeChat}
        messages={messages}
        sendMessage={sendMessage}
        show={params.chat === ChatType.Employee}
        currentDay={currentDate}
        canSeeConfidentialInfo={canSeeConfidentialInfo}
        isChatPage={false}
      />
      <Chat
        title={`${currentLanguage.InternalMessages}, ${formatter.dayMonth(shift.period.from)}`}
        goBack={closeChat}
        messages={internalMessages}
        sendMessage={sendInternalMessage}
        show={params.chat === ChatType.Private}
        currentDay={currentDate}
        canSeeConfidentialInfo={canSeeConfidentialInfo}
        isChatPage={false}
      />
      <SwapView
        onBackClick={() => {
          setSwapViewOpen(false);
        }}
        show={isSwapViewOpen}
        swapDetails={swapDetails}
        employees={employees}
        team={team}
        isSwap={isSwap}
        pendingAcceptRequest={pendingAcceptRequest}
        pendingRejectRequest={pendingRejectRequest}
        selectedExchangeId={selectedExchangeId}
        acceptRequestEnabled={acceptRequestEnabled}
        onSelectExchangeId={(exchangeId) => {
          exchangeSelectExchangeId(exchangeId);
        }}
        getEmployees={callGetEmployees}
        onAcceptExchange={() => {
          if (
            swapDetails?.requestInfo.personId &&
            (swapDetails.requestInfo.type === ExchangeTypeEnum.swapRequest ||
              selectedExchangeId)
          ) {
            const currentSwap = selectedExchangeId
              ? swapDetails.swapInfoList.find(
                  (f) => f.exchangeId === selectedExchangeId
                )
              : swapDetails.swapInfoList[0];

            if (currentSwap?.personId) {
              const offerName = employees[currentSwap.personId]?.name;
              const requestName =
                employees[swapDetails.requestInfo.personId]?.name;

              if (offerName && requestName) {
                callExchangeAccept(
                  swapDetails.requestInfo.unitId,
                  shift.period,
                  isSwap,
                  currentSwap.personId,
                  offerName,
                  currentSwap.exchangeId,
                  swapDetails.requestInfo.personId,
                  requestName
                );
              }
            }
          }
        }}
        onRejectExchange={() => {
          if (swapDetails) {
            callExchangeReject(
              swapDetails.requestInfo.exchangeId,
              shift.period,
              swapDetails.requestInfo.unitId,
              swapDetails.requestInfo.type,
              null,
              isSwap
            );
          }
        }}
      />
      <OfferView
        setActiveOfferView={setActiveOfferView}
        shift={shift}
        teams={teams}
        teamId={team.id}
        onBackClick={() => {
          setOfferViewOpen(false);
        }}
        possibleCandidateFilters={possibleCandidateFilters}
        defaultExchangeCandidateFilter={defaultExchangeCandidateFilter}
        acceptOffer={(teamIdList, candidateFilter, remark) => {
          callOfferOnShiftExchange(
            shift.id,
            team.id,
            teamIdList,
            candidateFilter,
            remark
          );
        }}
        rejectOffer={() => {
          setOfferViewOpen(false);
        }}
        offerViewActiveView={offerViewActiveView}
        dutyLines={dutyLines}
        employees={employees}
      />
      <ConfirmDeleteModal
        shown={showConfirmDeleteShift}
        shift={shift}
        loading={deleteShiftLoading}
        modifiedDutylines={
          shiftDef &&
          calculateModifiedDutylines(
            shift.dutyLinesOriginal?.length
              ? shift.dutyLinesOriginal
              : shift.dutyLines,
            shiftDef
          )
        }
        onCancel={() => {
          setConfirmDeleteModal(false);
        }}
        onOk={(shiftTimeLabel) => {
          void attemptRemoveShift(
            shiftDraft?.type === ShiftDraftType.Edited
              ? shiftDraft.originalShift.id
              : shift.id,
            team.id,
            employee?.name ?? '',
            shift.label,
            shiftTimeLabel
          ).then(() => {
            navigate(getSiteRoutes().team(team.id));
          });
        }}
        coudNotFetchDeletedActivities={coudNotFetchDeletedActivities}
        deletedActivities={deletedActivities}
        employee={employee}
      />
    </>
  );
};

export default ShiftView;

